import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Search, X, Loader2, MapPin, Building2, Users, Phone,
  StickyNote, CheckCircle2, CircleOff
} from 'lucide-react';
import { toast } from 'sonner';
import { UKPostcodeMap, POSTCODE_REGIONS, AREA_COLORS } from './UKPostcodeMap';
import './ProspectsMap.css';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProspectsMap() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const mainRef = useRef(null);

  // Bring the detail card + map into view when a prospect is picked.
  useEffect(() => {
    if (selected) mainRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selected]);

  useEffect(() => {
    axios.get(`${API}/api/prospects`)
      .then((res) => setProspects(res.data.prospects || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load prospects'))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(
    () => [...new Set(prospects.map((p) => p.group).filter(Boolean))].sort(),
    [prospects]
  );

  const filtered = useMemo(() => {
    let list = prospects;
    if (groupFilter !== 'all') list = list.filter((p) => p.group === groupFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        p.agency.toLowerCase().includes(q) ||
        (p.group || '').toLowerCase().includes(q) ||
        (p.postcodes || []).some((c) => c.toLowerCase() === q)
      );
    }
    return list;
  }, [prospects, query, groupFilter]);

  const highlight = useMemo(
    () => (selected ? new Set(selected.postcodes || []) : null),
    [selected]
  );

  const offMapCodes = selected
    ? (selected.postcodes || []).filter((c) => !POSTCODE_REGIONS[c])
    : [];

  return (
    <div className="prospects-map" data-testid="prospects-map">
      <div className="pmx-layout">
        {/* ── Left: search + client list ── */}
        <aside className="pmx-side">
          <div className="pmx-search">
            <Search size={15} />
            <input
              type="text"
              placeholder="Search prospects by name, group or postcode…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="prospects-search-input"
            />
            {query && (
              <button onClick={() => setQuery('')} className="pmx-search-clear"><X size={13} /></button>
            )}
          </div>

          <div className="pmx-group-pills">
            <button
              className={`pmx-pill ${groupFilter === 'all' ? 'is-active' : ''}`}
              onClick={() => setGroupFilter('all')}
              data-testid="prospects-group-all"
            >
              All <span>{prospects.length}</span>
            </button>
            {groups.map((g) => (
              <button
                key={g}
                className={`pmx-pill ${groupFilter === g ? 'is-active' : ''}`}
                onClick={() => setGroupFilter(g)}
                data-testid={`prospects-group-${g}`}
              >
                {g}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="pmx-loading"><Loader2 size={22} className="pmx-spin" /> Loading prospects…</div>
          ) : (
            <div className="pmx-list" data-testid="prospects-list">
              {filtered.length === 0 && <p className="pmx-empty">No prospects match.</p>}
              {filtered.map((p) => (
                <button
                  key={p.id}
                  className={`pmx-item ${selected?.id === p.id ? 'is-selected' : ''}`}
                  onClick={() => setSelected(selected?.id === p.id ? null : p)}
                  data-testid={`prospect-item-${p.id}`}
                >
                  <div className="pmx-item-top">
                    <span className="pmx-item-name">{p.agency}</span>
                    {p.active_wms
                      ? <span className="pmx-wms is-on" title="Active WMS account"><CheckCircle2 size={11} /> WMS</span>
                      : <span className="pmx-wms" title="No active WMS account"><CircleOff size={11} /></span>}
                  </div>
                  <div className="pmx-item-meta">
                    <span className="pmx-item-group">{p.group}</span>
                    {p.num_branches != null && <span><Building2 size={11} /> {p.num_branches}</span>}
                    <span><MapPin size={11} /> {(p.postcodes || []).length} postcodes</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* ── Right: detail + map ── */}
        <main className="pmx-main" ref={mainRef}>
          {selected ? (
            <div className="pmx-detail" data-testid="prospect-detail">
              <div className="pmx-detail-head">
                <div>
                  <h2>{selected.agency}</h2>
                  <div className="pmx-detail-sub">
                    <span className="pmx-item-group">{selected.group}</span>
                    {selected.sales_lettings && <span>{selected.sales_lettings}</span>}
                    {selected.num_branches != null && <span><Building2 size={12} /> {selected.num_branches} branches</span>}
                    {selected.regions?.length > 0 && <span><Users size={12} /> {selected.regions.join(' · ')}</span>}
                  </div>
                </div>
                <button className="pmx-clear-btn" onClick={() => setSelected(null)} data-testid="prospect-detail-close">
                  <X size={14} /> Clear
                </button>
              </div>
              <div className="pmx-detail-codes">
                {(selected.postcodes || []).map((c) => {
                  const info = POSTCODE_REGIONS[c];
                  return (
                    <span
                      key={c}
                      className="pmx-code-chip"
                      style={info ? { background: AREA_COLORS[info.area], color: info.area === 1 ? '#4a3c00' : '#fff' } : {}}
                      title={info ? `${info.region} — Area ${info.area}` : 'Outside coverage regions'}
                    >
                      {c}
                    </span>
                  );
                })}
              </div>
              {offMapCodes.length > 0 && (
                <p className="pmx-offmap">Outside mapped regions: {offMapCodes.join(', ')}</p>
              )}
              {(selected.group_contact || selected.brand_contact) && (
                <p className="pmx-contact"><Phone size={12} /> {[selected.group_contact, selected.brand_contact].filter(Boolean).join(' · ')}</p>
              )}
              {selected.notes && <p className="pmx-notes"><StickyNote size={12} /> {selected.notes}</p>}
            </div>
          ) : (
            <div className="pmx-detail pmx-detail-hint" data-testid="prospect-detail-hint">
              <MapPin size={16} />
              <span>Select a prospect to flash the postcode areas their branches serve.</span>
            </div>
          )}

          <UKPostcodeMap highlight={highlight} />

          <div className="pmx-legend" data-testid="map-legend">
            {[1, 2, 3, 4, 5].map((a) => (
              <span key={a} className="pmx-legend-item">
                <i style={{ background: AREA_COLORS[a] }} /> Area {a}
              </span>
            ))}
            <span className="pmx-legend-item"><i style={{ background: '#6b6d70' }} /> Orbital</span>
            <span className="pmx-legend-item"><i style={{ background: 'rgba(255,255,255,0.16)' }} /> Outside regions</span>
          </div>
        </main>
      </div>
    </div>
  );
}
